import {
	API_ENDPOINTS,
	customToast,
	route,
	FormInput,
	ImageType,
	Modal,
	replaceKeyWithValue,
	StringHelpers,
	useLangContext,
	Variants,
	useDisabledContext,
} from 'carrier-fe';
import { useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { ViessmannInstallation } from '../types/installation';
import {
	SYSTEM_TYPES,
	UPLIFT_YEARS,
	VIPARTNER_YEARS,
} from '../util/Constants';

function WarrantyUpliftModal({
	modalOpen,
	modalClose,
	onSuccess,
	storeData,
	pageLang,
}: {
	modalOpen: boolean;
	modalClose: () => void;
	onSuccess?: () => void;
	storeData: ViessmannInstallation;
	pageLang: any;
}) {
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { installationId } = useParams();

	const [years, setYears] = useState<string>('');
	const [sendCertificate, setSendCertificate] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const lang = pageLang?.page?.warranty_uplift_modal;

	const isViPartner =
		storeData?.system_type === SYSTEM_TYPES.HEAT_PUMPS &&
		Number(years) === VIPARTNER_YEARS;

	const handleYearsChange = (
		value: string | boolean | ImageType | string[] | null
	) => {
		if (typeof value === 'string') {
			setYears(value);
		}
	};

	const handleSendCertificateChange = (
		value: string | boolean | ImageType | string[] | null
	) => {
		if (typeof value === 'boolean') {
			setSendCertificate(value);
		}
	};

	const handleClose = () => {
		setYears('');
		setSendCertificate(false);
		modalClose();
	};

	const handleConfirm = async () => {
		if (!years) {
			customToast({
				title: StringHelpers.title(
					replaceKeyWithValue(
						crud?.placeholders?.select || 'Select :model',
						'model',
						lang?.years || 'warranty length'
					)
				),
				variant: Variants.Danger,
			});
			return;
		}

		try {
			setIsSubmitting(true);
			setDisabled(true);

			const response = await axios.post(
				route(API_ENDPOINTS.REGISTRATION.INSTALLATION.WARRANTY_UPLIFT, {
					installationId: String(installationId),
				}),
				{
					years: Number(years),
					send_certificate: sendCertificate,
				}
			);

			customToast({
				title:
					response?.data?.message ||
					'Success. Warranty has been updated.',
				variant: Variants.Success,
			});

			onSuccess?.();
			handleClose();
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'There was an error registering the warranty uplift, please try again or contact support.',
				variant: Variants.Danger,
			});
		} finally {
			setIsSubmitting(false);
			setDisabled(false);
		}
	};

	return (
		<Modal
			open={modalOpen}
			onClose={handleClose}
			title={StringHelpers.title(
				lang?.title || 'Register Warranty Uplift'
			)}
			confirmText={
				isSubmitting
					? crud?.buttons?.save?.submitting || 'Saving...'
					: crud?.buttons?.save?.default || 'Save'
			}
			closeText={crud?.buttons?.cancel?.default || 'Cancel'}
			onConfirm={handleConfirm}
			disabled={disabled || isSubmitting}
			variant={Variants.Danger}
		>
			<p>
				{lang?.intro ||
					'You are about to change the warranty length for this installation. Any existing warranty will be replaced, and the change will be pushed to SAP where applicable.'}
			</p>

			<FormInput
				type="select"
				label={StringHelpers.title(
					lang?.years || 'Warranty length (years)'
				)}
				name="years"
				value={years}
				onChange={handleYearsChange}
				placeholder={StringHelpers.title(
					replaceKeyWithValue(
						crud?.placeholders?.select || 'Select :model',
						'model',
						lang?.years || 'warranty length'
					)
				)}
				options={UPLIFT_YEARS.map((option) => ({
					label: `${option} ${fields?.years || 'Years'}`,
					value: String(option),
				}))}
				required
			/>

			<FormInput
				type="checkbox"
				label={StringHelpers.title(
					lang?.send_certificate ||
						'Send warranty certificate to the customer'
				)}
				name="send_certificate"
				value={sendCertificate}
				onChange={handleSendCertificateChange}
			/>
			<small className="text-muted d-block mb-3">
				{lang?.certificate_note ||
					'The certificate is only emailed when customer emails are enabled for the installer company (or the installation was registered by a householder).'}
			</small>

			<FormInput
				type="checkbox"
				label={StringHelpers.title(lang?.vipartner || 'ViPartner scheme')}
				name="vipartner"
				value={isViPartner}
				onChange={() => {}}
				disabled
			/>
			<small className="text-muted d-block mb-3">
				{lang?.vipartner_hint ||
					'Applied automatically for heat pump installations with a 7 year uplift.'}
			</small>

			<p className="mt-2">
				{lang?.confirm_question ||
					'Are you sure you want to register this warranty uplift?'}
			</p>
		</Modal>
	);
}

export default WarrantyUpliftModal;
